import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function UserMenu() {
  const { user, profile, loading, signOut, openAuthModal } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // During auth loading, show placeholder to avoid flash of Login button for logged-in users
  if (loading) {
    return (
      <div className="w-28 h-10 rounded-md bg-white/5 border border-white/10 animate-pulse"></div>
    );
  }

  // Not logged in → show Login button
  if (!user) {
    return (
      <button
        onClick={openAuthModal}
        className="btn-liquid-gold flex items-center gap-2 text-[#0A0A0F] text-emboss-dark px-4 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-user-line text-sm"></i>
        </span>
        <span>Login / Entrar</span>
      </button>
    );
  }

  const initials = (profile?.full_name || user.email || 'U')
    .split(' ')
    .map((w: string) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 cursor-pointer"
        aria-label="Menu do usuário"
      >
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.full_name || ''}
            className="w-9 h-9 rounded-full border-2 border-[#D4AF37] object-cover"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center text-[#0A0A0F] font-bold text-sm border-2 border-[#D4AF37]">
            {initials}
          </div>
        )}
        <span className="text-gray-300 text-sm hidden lg:block max-w-[120px] truncate">
          {profile?.full_name?.split(' ')[0] || 'Minha conta'}
        </span>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-sm`}></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-52 bg-[#0F1419] border border-white/10 rounded-xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-white text-sm font-semibold truncate">{profile?.full_name || 'Usuário'}</p>
            <p className="text-gray-400 text-xs truncate">{profile?.email || user.email}</p>
          </div>
          <button
            onClick={() => { navigate('/meu-perfil'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-[#D4AF37] hover:bg-white/5 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-user-settings-line text-base"></i>
            <span>Meu Perfil</span>
          </button>
          <button
            onClick={() => { navigate('/dashboard'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-[#D4AF37] hover:bg-white/5 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-dashboard-line text-base"></i>
            <span>Minha Área</span>
          </button>
          <button
            onClick={() => { navigate('/dashboard'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-[#D4AF37] hover:bg-white/5 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-book-open-line text-base"></i>
            <span>Meus Cursos</span>
          </button>
          <button
            onClick={() => { navigate('/certificados'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-[#D4AF37] hover:bg-white/5 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-award-line text-base"></i>
            <span>Certificados</span>
          </button>
          <button
            onClick={() => { signOut(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors text-sm cursor-pointer border-t border-white/10"
          >
            <i className="ri-logout-box-r-line text-base"></i>
            <span>Sair</span>
          </button>
        </div>
      )}
    </div>
  );
}
